import styled from 'styled-components';
import { colors, shadows, radius, space, lines } from '@ardoq/design-tokens';
import { ButtonType, GhostButton } from '@ardoq/button';
import { ButtonWithDropdown, DropdownItem } from '@ardoq/dropdown-menu';
import { Icon, IconName } from '@ardoq/icons';
import { FlexBox } from '@ardoq/layout';

type VerticalPosition = 'top' | 'bottom';
type HorizontalPosition = 'left' | 'center' | 'right';

export type FloatingToolBarPosition = {
  vertical: VerticalPosition;
  horizontal: HorizontalPosition;
};

type ActionItem = {
  type: 'action';
  /** For react key */
  key: string;
  label: string;
  icon?: IconName;
  onClick: () => void;
  isActive?: boolean;
  isDisabled?: boolean;
  tooltip?: string;
  dataTestId?: string;
};

type MenuItem = {
  type: 'menu';
  /** For react key */
  key: string;
  label: string;
  icon?: IconName;
  options: Partial<DropdownItem>[];
  isDisabled?: boolean;
  tooltip?: string;
  dataTestId?: string;
};

export type FloatingToolBarItem = ActionItem | MenuItem;

export type FloatingToolBarProps = {
  items: FloatingToolBarItem[];
  position?: FloatingToolBarPosition;
  /** Shifts the horizontal center point by this CSS value, e.g. '-250px' */
  centerOffset?: string;
};

type ToolBarContainerProps = {
  $position: FloatingToolBarPosition;
  $centerOffset?: string;
};

type PositionStyle = {
  top: string;
  left: string;
  transform: string;
};

const positionStyleMap: Record<
  VerticalPosition,
  Record<HorizontalPosition, PositionStyle>
> = {
  top: {
    left: {
      top: space.medium,
      left: space.medium,
      transform: '',
    },
    center: {
      top: space.medium,
      left: '50%',
      transform: 'translateX(-50%)',
    },
    right: {
      top: space.medium,
      left: `calc(100% - ${space.medium})`,
      transform: 'translateX(-100%)',
    },
  },
  bottom: {
    left: {
      top: `calc(100% - ${space.medium})`,
      left: space.medium,
      transform: 'translateY(-100%)',
    },
    center: {
      top: `calc(100% - ${space.medium})`,
      left: '50%',
      transform: 'translateX(-50%) translateY(-100%)',
    },
    right: {
      top: `calc(100% - ${space.medium})`,
      left: `calc(100% - ${space.medium})`,
      transform: 'translateX(-100%) translateY(-100%)',
    },
  },
};

const PositionContainer = styled(FlexBox).attrs({
  gap: 'small',
})<ToolBarContainerProps>`
  ${({ $position, $centerOffset }) => {
    const { top, left, transform } =
      positionStyleMap[$position.vertical][$position.horizontal];

    const adjustedLeft =
      $centerOffset && $position.horizontal === 'center'
        ? `calc(50% + ${$centerOffset})`
        : left;

    return `
      top: ${top};
      left: ${adjustedLeft};
      transform: ${transform};
    `;
  }}
  position: absolute;
  transition:
    top 0.3s ease,
    left 0.3s ease,
    transform 0.3s ease;
  background: ${colors.bgDefault};
  border-radius: ${radius.large};
  padding: ${space.small};
  box-shadow: ${shadows.shadowM};
  border: ${lines.solidGrey1};
  z-index: 1;
`;

const StyledAction = styled(GhostButton)<{ $active?: boolean }>`
  ${props =>
    !props.isDisabled &&
    props.$active &&
    `
      background-color: ${colors.surfaceActionPrimaryFocused};
      color: ${colors.textOnInvertedStrong};
      svg {
        color: ${colors.textOnInvertedStrong} !important;
      }
      &:hover{
        background-color: ${colors.surfaceActionPrimaryHover};
        color: ${colors.textOnInvertedStrong};
      }
    `}
`;

// The parent of floating toolbar must have a reltive position
export const FloatingToolbarContainer = styled.div`
  position: relative;
`;

export const FloatingToolBar = ({
  items,
  position = { vertical: 'bottom', horizontal: 'center' },
  centerOffset,
}: FloatingToolBarProps) => {
  return (
    <PositionContainer $position={position} $centerOffset={centerOffset}>
      {items.map(item => {
        if (item.type === 'action') {
          return (
            <StyledAction
              key={item.key}
              onClick={item.onClick}
              isDisabled={item.isDisabled}
              dataTestId={item.dataTestId}
              $active={item.isActive}
              data-tooltip-text={item.tooltip}
            >
              {item.icon && <Icon iconName={item.icon} />}
              {item.label}
            </StyledAction>
          );
        }

        return (
          <ButtonWithDropdown
            key={item.key}
            buttonType={ButtonType.GHOST}
            options={item.options}
            isDisabled={item.isDisabled}
            dataTestId={item.dataTestId}
            iconName={item.icon}
            tooltipText={item.tooltip}
          >
            {item.label}
          </ButtonWithDropdown>
        );
      })}
    </PositionContainer>
  );
};
